import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreditCard, DollarSign, ListChecks } from 'lucide-react';
import purchaseService from '../services/purchaseService';
import paymentService from '../services/paymentService';
import { PurchaseResponseDTO } from '../types/purchase';
import { PaymentType, PaymentResponseDTO, PaymentStatus } from '../types/payment';
import alertService from '../services/alertService';
import { formatFromRaw, getNextRawOnKey, rawFromNumber } from '../utils/decimalMask';

const currency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

// Extrai a mensagem detalhada retornada pela API
const getApiErrorMessage = (error: any): string => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.message) return String(data.message);
  if (data?.error) return String(data.error);
  if (error?.message) return String(error.message);
  return 'Tente novamente mais tarde.';
};

const isPaidStatus = (status: any) => ['EFETUADO', 'PAGO'].includes(String(status).toUpperCase());
const isPendingStatus = (status: any) => String(status).toUpperCase() === 'PENDENTE';

const PurchasePaymentV2Page: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [purchase, setPurchase] = useState<PurchaseResponseDTO | null>(null);
  const [types, setTypes] = useState<PaymentType[]>([]);
  const [payments, setPayments] = useState<PaymentResponseDTO[]>([]);
  const [rows, setRows] = useState<Record<string, { valor: string; observacoes: string; pendingId?: string }>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [valorRawMap, setValorRawMap] = useState<Record<string, string>>({});
  const VALOR_DECIMALS = 2;

  const totalPaid = useMemo(
    () => payments.filter(p => isPaidStatus(p.status)).reduce((sum, p) => sum + (p.valor || 0), 0),
    [payments]
  );
  const remaining = useMemo(() => (purchase?.valorTotal || 0) - totalPaid, [purchase, totalPaid]);
  const hasPending = useMemo(() => payments.some(p => isPendingStatus(p.status)), [payments]);

  const loadAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [purchaseRes, typesRes, paymentsRes] = await Promise.all([
        purchaseService.getById(id),
        paymentService.getTypes(),
        paymentService.listByPurchasePath(id),
      ]);
      setPurchase(purchaseRes.data);
      const resolvedTypes = Array.isArray(typesRes.data)
        ? typesRes.data
        : (typesRes.data as any)?.content || [];
      setTypes(resolvedTypes);
      const raw = paymentsRes.data as any;
      const loadedPayments = (Array.isArray(raw) ? raw : raw?.content || []) as PaymentResponseDTO[];
      setPayments(loadedPayments);

      // Construir linhas da tabela a partir dos tipos e preencher com pagamentos pendentes
      const pendingByType: Record<string, PaymentResponseDTO | undefined> = {};
      for (const p of loadedPayments) {
        if (isPendingStatus(p.status)) {
          pendingByType[p.tipoPagamentoId] = p;
        }
      }
      const initialRows: Record<string, { valor: string; observacoes: string; pendingId?: string }> = {};
      for (const t of resolvedTypes) {
        const existing = pendingByType[t.id];
        initialRows[t.id] = {
          valor: existing && typeof existing.valor === 'number' ? String(existing.valor) : '',
          observacoes: existing?.observacoes || '',
          pendingId: existing?.id,
        };
        // Inicializa o raw map com base no valor numérico existente
        const baseNumber = existing && typeof existing.valor === 'number' ? existing.valor : 0;
        setValorRawMap(prev => ({ ...prev, [t.id]: rawFromNumber(baseNumber, VALOR_DECIMALS) }));
      }
      setRows(initialRows);
    } catch (error) {
      alertService.error('Erro ao carregar pagamentos', getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  const handleSaveRows = async () => {
    if (!id) return;
    try {
      alertService.loading('Salvando alterações...');
      // Mapa de pendentes atuais por tipo
      const currentPending: Record<string, PaymentResponseDTO | undefined> = {};
      for (const p of payments) {
        if (p.status === 'PENDENTE') currentPending[p.tipoPagamentoId] = p;
      }

      // Processar cada tipo
      for (const t of types) {
        const row = rows[t.id];
        if (!row) continue;
        const newValue = row.valor ? parseFloat(row.valor.replace(',', '.')) : 0;
        const newObs = row.observacoes?.trim() || undefined;
        const existing = currentPending[t.id];

        if (existing) {
          const unchanged = (
            (typeof existing.valor === 'number' ? existing.valor : 0) === (isNaN(newValue) ? 0 : newValue)
          ) && ((existing.observacoes || '').trim() === (newObs || ''));

          if (isNaN(newValue) || newValue <= 0) {
            // Valor zerado: cancelar pendente
            const cancelRes = await paymentService.cancel(existing.id, 'Removido pela tabela de pagamentos');
            // Atualizar lista local
            setPayments(prev => prev.map(p => (p.id === existing.id ? cancelRes.data : p)));
            continue;
          }

          if (!unchanged) {
            // Cancelar e recriar com novos dados
            await paymentService.cancel(existing.id, 'Atualizado pela tabela de pagamentos');
            const createRes = await paymentService.create({
              compraId: id,
              tipoPagamentoId: t.id,
              valor: newValue,
              observacoes: newObs,
            });
            // Substituir o pagamento cancelado pelo novo registro
            setPayments(prev => prev.map(p => (p.id === existing.id ? createRes.data : p)));
          }
        } else {
          // Não existe pendente: criar se valor válido
          if (!isNaN(newValue) && newValue > 0) {
            const createRes = await paymentService.create({
              compraId: id,
              tipoPagamentoId: t.id,
              valor: newValue,
              observacoes: newObs,
            });
            setPayments(prev => [...prev, createRes.data]);
          }
        }
      }

      alertService.close();
      alertService.success('Alterações salvas');
      // Recarregar dados para sincronizar pendentes/efetuados
      await loadAll();
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao salvar alterações', getApiErrorMessage(error));
    }
  };

  const handleEfetuar = async (paymentId: string) => {
    try {
      alertService.loading('Confirmando pagamento...');
      const res = await paymentService.efetuar(paymentId);
      alertService.close();
      setPayments(prev => prev.map(p => (p.id === paymentId ? res.data : p)));
      alertService.success('Pagamento confirmado');
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao confirmar pagamento', getApiErrorMessage(error));
    }
  };

  const handleEstornar = async (paymentId: string) => {
    const confirm = await alertService.confirm('Estornar pagamento?', 'Esta ação irá cancelar o pagamento.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Estornando pagamento...');
      const res = await paymentService.cancel(paymentId, 'Estorno pelo usuário');
      alertService.close();
      setPayments(prev => prev.map(p => (p.id === paymentId ? res.data : p)));
      alertService.success('Pagamento estornado');
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao estornar pagamento', getApiErrorMessage(error));
    }
  };

  const handleFinalizePurchase = async () => {
    if (!id) return;
    if (remaining > 0 || hasPending) {
      alertService.warning('Finalize os pagamentos antes de concluir a compra.');
      return;
    }
    const confirm = await alertService.confirm('Finalizar compra?', 'A compra será marcada como finalizada.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Finalizando compra...');
      await purchaseService.confirm(id);
      alertService.close();
      alertService.success('Compra finalizada');
      // Após finalizar na tela de pagamento, redirecionar para a visualização da compra
      navigate(`/compras/${id}/visualizar`);
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao finalizar compra', getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho da página com ícone seguindo padrão */}
      <div className="flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-red-600" />
        <h1 className="text-xl font-semibold text-gray-900">Pagamentos da Compra</h1>
      </div>

      {/* Card: Pagamentos por Tipo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-gray-600" />
          <h2 className="text-sm font-semibold text-gray-800">Pagamentos por Tipo</h2>
        </div>
        <div className="p-4">
          <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Tipo de Pagamento</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Valor</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Observações</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {types.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-gray-500 px-3 py-6">Nenhum tipo disponível</td>
                  </tr>
                ) : (
                  types.map((t) => (
                    <tr key={t.id} className="border-t">
                      <td className="px-3 py-2 text-gray-800">{t.nome}</td>
                      <td className="px-3 py-2">
                        <input
                          className="w-40 border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          type="text"
                          inputMode="numeric"
                          placeholder={formatFromRaw(rawFromNumber(0, VALOR_DECIMALS), VALOR_DECIMALS)}
                          value={formatFromRaw(
                            valorRawMap[t.id] ?? rawFromNumber(
                              rows[t.id]?.valor ? parseFloat(String(rows[t.id]?.valor).replace(',', '.')) || 0 : 0,
                              VALOR_DECIMALS
                            ),
                            VALOR_DECIMALS
                          )}
                          onKeyDown={(e) => {
                            const currentRaw = valorRawMap[t.id] ?? rawFromNumber(
                              rows[t.id]?.valor ? parseFloat(String(rows[t.id]?.valor).replace(',', '.')) || 0 : 0,
                              VALOR_DECIMALS
                            );
                            const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                            if (!handled) return;
                            e.preventDefault();
                            setValorRawMap(prev => ({ ...prev, [t.id]: nextRaw }));
                            const nextFmt = formatFromRaw(nextRaw, VALOR_DECIMALS);
                            setRows(prev => ({
                              ...prev,
                              [t.id]: { ...(prev[t.id] || { valor: '', observacoes: '' }), valor: nextFmt }
                            }));
                          }}
                          onChange={(e) => {
                            const val = e.target.value || '';
                            const numeric = parseFloat(val.replace(',', '.')) || 0;
                            setValorRawMap(prev => ({ ...prev, [t.id]: rawFromNumber(numeric, VALOR_DECIMALS) }));
                            setRows(prev => ({ ...prev, [t.id]: { ...(prev[t.id] || { valor: '', observacoes: '' }), valor: val } }));
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Opcional"
                          value={rows[t.id]?.observacoes ?? ''}
                          onChange={(e) => setRows(prev => ({ ...prev, [t.id]: { ...(prev[t.id] || { valor: '', observacoes: '' }), observacoes: e.target.value } }))}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
              onClick={handleSaveRows}
            >
              Salvar alterações
            </button>
          </div>
        </div>
      </div>

      {/* Card: Pagamentos cadastrados */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-gray-600" />
          <h2 className="text-sm font-semibold text-gray-800">Pagamentos cadastrados</h2>
        </div>
        <div className="p-4">
          <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Valor</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Observações</th>
                  <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 px-3 py-6">Nenhum pagamento cadastrado</td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="px-3 py-2 text-gray-800">{p.tipoPagamentoNome || p.tipoPagamentoId}</td>
                      <td className="px-3 py-2">{currency(p.valor)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-sm ${
                          isPaidStatus(p.status) ? 'bg-green-100 text-green-700' : String(p.status).toUpperCase() === 'CANCELADO' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{p.observacoes || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          {p.status === 'PENDENTE' && (
                            <button
                              className="px-3 py-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm"
                              onClick={() => handleEfetuar(p.id)}
                            >
                              Confirmar
                            </button>
                          )}
                          {p.status !== 'CANCELADO' && (
                            <button
                              className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm"
                              onClick={() => handleEstornar(p.id)}
                            >
                              Estornar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Card: Resumo e Finalização */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-gray-600" />
          <h2 className="text-sm font-semibold text-gray-800">Resumo e Finalização</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-100 rounded-md p-3">
              <div className="text-sm text-gray-600">Valor Total</div>
              <div className="text-red-600 text-xl font-semibold">{currency(purchase?.valorTotal || 0)}</div>
            </div>
            <div className="bg-gray-100 rounded-md p-3">
              <div className="text-sm text-gray-600">Valor Pago</div>
              <div className="text-gray-800 text-xl font-semibold">{currency(totalPaid)}</div>
            </div>
            <div className="bg-gray-100 rounded-md p-3">
              <div className="text-sm text-gray-600">Valor Restante</div>
              <div className="text-green-600 text-xl font-semibold">{currency(Math.max(remaining, 0))}</div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              className={`px-4 py-2 rounded-lg font-medium ${
                remaining <= 0 && !hasPending ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
              onClick={handleFinalizePurchase}
              disabled={remaining > 0 || hasPending}
            >
              Finalizar Compra
            </button>
          </div>
        </div>
        <div className="bg-gray-100 text-gray-700 px-4 py-2 flex items-center justify-between">
          <span className="text-sm">Ajuda (F1)</span>
          <span className="text-sm">Sair (ESC)</span>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center">
          <div className="bg-white rounded-md shadow px-5 py-3">Carregando...</div>
        </div>
      )}
    </div>
  );
};

export default PurchasePaymentV2Page;